'use client';

import { useState, useCallback, useMemo } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  IconButton,
  Button,
  Tooltip,
  Badge,
  LegacyDialog,
  Input,
  Spinner,
} from '@terra/ui-design-system';
import {
  Alert01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUpDownIcon,
  SourceCodeIcon,
  Copy01Icon,
  Edit02Icon,
  Key01Icon,
  Add01Icon,
  PowerIcon,
  PowerOffIcon,
  SparklesIcon,
  Delete02Icon,
  VariableIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import {
  usePreRequestScriptStore,
  validateScriptSyntax,
  type ScriptPreset,
} from '../../store/preRequestScriptStore';
import { useThemeMode } from '@terra/ui-design-system';
import {
  createTerraAutocomplete,
  getTerraEditorThemeByMode,
} from '../../utils/terraEditorExtensions';
import {
  useApplicationCredentials,
  type ApplicationCredential,
} from '../../hooks/useApplicationCredentials';
import { useTranslations } from 'next-intl';

const EMPTY_SCRIPT_PRESETS: ScriptPreset[] = [];

type ScriptPresetsManagerProps = {
  applicationId: string;
  onSelectPreset: (presetId: string | null) => void;
};

export function ScriptPresetsManager({
  applicationId,
  onSelectPreset,
}: ScriptPresetsManagerProps) {
  const tScripts = useTranslations('replay.scripts');
  const [isOpen, setIsOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<ScriptPreset | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const appConfig = usePreRequestScriptStore(
    (state) => state.scriptsByApplication[applicationId],
  );
  const presets = appConfig?.presets ?? EMPTY_SCRIPT_PRESETS;
  const activePresetId = appConfig?.activePresetId ?? null;
  const activePresetName = useMemo(
    () => presets.find((p) => p.id === activePresetId)?.name,
    [presets, activePresetId],
  );
  const { createPreset, updatePreset, deletePreset, duplicatePreset } =
    usePreRequestScriptStore();
  const handleSelect = useCallback(
    (preset: ScriptPreset) => {
      onSelectPreset(preset.id === activePresetId ? null : preset.id);
      setIsOpen(false);
    },
    [activePresetId, onSelectPreset],
  );
  const handleDuplicate = useCallback(
    (preset: ScriptPreset) => {
      const newPreset = duplicatePreset(applicationId, preset.id);

      if (newPreset) {
        setEditingPreset(newPreset);
      }
    },
    [applicationId, duplicatePreset],
  );
  const handleDelete = useCallback(
    (preset: ScriptPreset) => {
      deletePreset(applicationId, preset.id);
    },
    [applicationId, deletePreset],
  );
  const handleEdit = useCallback((preset: ScriptPreset) => {
    setEditingPreset(preset);
    setIsCreating(false);
  }, []);
  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setEditingPreset({
      id: '',
      name: '',
      script: '',
      variables: {},
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, []);
  const handleSave = useCallback(
    (preset: ScriptPreset) => {
      if (isCreating) {
        const created = createPreset(applicationId, {
          name: preset.name,
          script: preset.script,
          variables: preset.variables,
        });

        onSelectPreset(created.id);
      } else {
        updatePreset(applicationId, preset.id, {
          name: preset.name,
          script: preset.script,
          variables: preset.variables,
        });
      }

      setEditingPreset(null);
      setIsCreating(false);
      setIsOpen(false);
    },
    [isCreating, applicationId, createPreset, updatePreset, onSelectPreset],
  );
  const handleCancel = useCallback(() => {
    setEditingPreset(null);
    setIsCreating(false);
  }, []);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => setIsOpen(true)}
        css={{
          height: '24px',
          paddingInline: '8px',
          fontSize: 'xs',
          fontWeight: 'normal',
          gap: '4px',
          maxWidth: '120px',
        }}
      >
        <SourceCodeIcon size={12} />
        <Text fontSize="xs" truncate>
          {activePresetName || tScripts('selectScriptPlaceholder')}
        </Text>
        <ArrowUpDownIcon size={10} />
      </Button>

      <LegacyDialog.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content
            maxW="640px"
            w="90vw"
            p="0"
            overflow="hidden"
            maxH="85vh"
            display="flex"
            flexDirection="column"
          >
            <HStack
              justifyContent="space-between"
              alignItems="center"
              px="3"
              py="2"
              borderBottomWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
              flexShrink={0}
            >
              <HStack gap="1.5" alignItems="center">
                <SourceCodeIcon size={14} />
                <LegacyDialog.Title>Script Presets</LegacyDialog.Title>
              </HStack>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton aria-label="Close" variant="ghost" size="xs">
                  <Cancel01Icon size={14} />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>

            <Box p="3" flex="1" overflow="auto">
              {editingPreset ? (
                <PresetEditor
                  preset={editingPreset}
                  isCreating={isCreating}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  applicationId={applicationId}
                />
              ) : (
                <VStack gap="2" alignItems="stretch">
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={handleCreate}
                    w="full"
                    h="8"
                  >
                    <Add01Icon size={13} />
                    Create New Script
                  </Button>

                  {presets.length === 0 ? (
                    <Box py="6" textAlign="center">
                      <Text fontSize="xs" color="fg.muted">
                        No scripts yet. Create one to get started.
                      </Text>
                    </Box>
                  ) : (
                    <Box maxH="460px" overflowY="auto">
                      <VStack gap="1.5" alignItems="stretch">
                        <Button
                          type="button"
                          variant={!activePresetId ? 'subtle' : 'ghost'}
                          size="xs"
                          justifyContent="flex-start"
                          h="8"
                          onClick={() => {
                            onSelectPreset(null);
                            setIsOpen(false);
                          }}
                        >
                          <Text fontSize="xs">{tScripts('noScript')}</Text>
                        </Button>
                        {presets.map((preset) => (
                          <PresetItem
                            key={preset.id}
                            preset={preset}
                            isActive={preset.id === activePresetId}
                            labels={{
                              deactivate: tScripts('deactivateScript'),
                              select: tScripts('selectScript'),
                              use: tScripts('useThisScript'),
                            }}
                            onSelect={handleSelect}
                            onEdit={handleEdit}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDelete}
                          />
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}

type PresetItemProps = {
  preset: ScriptPreset;
  isActive: boolean;
  labels: {
    deactivate: string;
    select: string;
    use: string;
  };
  onSelect: (preset: ScriptPreset) => void;
  onEdit: (preset: ScriptPreset) => void;
  onDuplicate: (preset: ScriptPreset) => void;
  onDelete: (preset: ScriptPreset) => void;
};

function PresetItem({
  preset,
  isActive,
  labels,
  onSelect,
  onEdit,
  onDuplicate,
  onDelete,
}: PresetItemProps) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const syntaxResult = useMemo(
    () => validateScriptSyntax(preset.script),
    [preset.script],
  );
  const previewExtensions = useMemo(
    () => [javascript(), getTerraEditorThemeByMode({ isDark })],
    [isDark],
  );

  return (
    <Box
      p="2"
      borderWidth="1px"
      borderColor={isActive ? 'border.accent' : 'border.default'}
      borderRadius="sm"
      bg={isActive ? 'bg.subtle' : 'bg.default'}
      _hover={{ bg: 'bg.subtle', borderColor: 'border.muted' }}
      transition="all 0.15s ease"
    >
      <VStack gap="1.5" alignItems="stretch">
        <HStack gap="1.5" alignItems="center" flexWrap="wrap">
          <Text fontSize="xs" fontWeight="medium">
            {preset.name || 'Untitled'}
          </Text>
          {isActive && (
            <Badge size="sm" variant="emphasis">
              Active
            </Badge>
          )}
          {!syntaxResult.valid && (
            <Tooltip title={syntaxResult.error || 'Syntax error'}>
              <Box color="fg.error" display="inline-flex">
                <Alert01Icon size={12} />
              </Box>
            </Tooltip>
          )}
        </HStack>

        <Box
          borderRadius="sm"
          borderWidth="1px"
          borderColor={syntaxResult.valid ? 'border.subtle' : 'border.error'}
          overflow="hidden"
          maxH="64px"
        >
          {preset.script ? (
            <CodeMirror
              value={preset.script}
              extensions={previewExtensions}
              editable={false}
              basicSetup={{
                lineNumbers: false,
                foldGutter: false,
                highlightActiveLine: false,
                highlightSelectionMatches: false,
                autocompletion: false,
              }}
              theme="none"
              style={{ fontSize: '10px', overflow: 'hidden' }}
            />
          ) : (
            <Box p="1.5">
              <Text fontSize="xs" fontFamily="mono" color="fg.subtle">
                (empty script)
              </Text>
            </Box>
          )}
        </Box>

        <HStack gap="0.5" justify="flex-end">
          <Tooltip title={isActive ? labels.deactivate : labels.use}>
            <IconButton
              aria-label={isActive ? labels.deactivate : labels.select}
              size="xs"
              variant="ghost"
              color={isActive ? 'fg.error' : undefined}
              onClick={() => onSelect(preset)}
            >
              {isActive ? <PowerOffIcon size={14} /> : <PowerIcon size={14} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit script">
            <IconButton
              aria-label="Edit"
              size="xs"
              variant="ghost"
              onClick={() => onEdit(preset)}
            >
              <Edit02Icon size={14} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Duplicate script">
            <IconButton
              aria-label="Duplicate"
              size="xs"
              variant="ghost"
              onClick={() => onDuplicate(preset)}
            >
              <Copy01Icon size={14} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete script">
            <IconButton
              aria-label="Delete"
              size="xs"
              variant="ghost"
              onClick={() => onDelete(preset)}
            >
              <Delete02Icon size={14} />
            </IconButton>
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );
}

type PresetEditorProps = {
  preset: ScriptPreset;
  isCreating: boolean;
  onSave: (preset: ScriptPreset) => void;
  onCancel: () => void;
  applicationId: string;
};

function PresetEditor({
  preset,
  isCreating,
  onSave,
  onCancel,
  applicationId,
}: PresetEditorProps) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [name, setName] = useState(preset.name);
  const [script, setScript] = useState(preset.script);
  const [variables, setVariables] = useState<Record<string, string>>(
    preset.variables,
  );
  const [showVariables, setShowVariables] = useState(
    Object.keys(preset.variables).length > 0,
  );
  const [showCredentials, setShowCredentials] = useState(false);
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const { credentials: applicationCredentials } = useApplicationCredentials({
    applicationId,
  });
  const credentialsWithData = useMemo(
    () =>
      applicationCredentials.filter(
        (credential) =>
          credential.secrets.length > 0 ||
          credential.headers.length > 0 ||
          credential.cookies.length > 0 ||
          credential.password,
      ),
    [applicationCredentials],
  );
  const syntaxResult = useMemo(() => validateScriptSyntax(script), [script]);
  const canSave = syntaxResult.valid;
  const handleSave = useCallback(() => {
    if (!canSave) return;
    const finalName = name.trim() || `Script ${Date.now()}`;

    onSave({
      ...preset,
      name: finalName,
      script,
      variables,
    });
  }, [canSave, name, script, variables, preset, onSave]);
  const handleGenerate = useCallback(async () => {
    const description = aiPrompt.trim();

    if (!description) return;
    if (!window.electronAPI?.replay?.generatePreRequestScript) {
      setAiError('AI generation is not available');

      return;
    }

    setAiGenerating(true);
    setAiError(null);

    try {
      const result = await window.electronAPI.replay.generatePreRequestScript({
        description,
        currentScript: script || undefined,
        applicationId,
      });

      if (result.success && result.script) {
        setScript(result.script);
        setAiPrompt('');
      } else {
        setAiError(result.error || 'Generation failed');
      }
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Generation failed');
    } finally {
      setAiGenerating(false);
    }
  }, [aiPrompt, script, applicationId]);
  const handleAddVariable = useCallback(() => {
    const trimmedKey = newVarKey.trim();

    if (!trimmedKey) return;
    setVariables((prev) => ({ ...prev, [trimmedKey]: newVarValue }));
    setNewVarKey('');
    setNewVarValue('');
  }, [newVarKey, newVarValue]);
  const handleRemoveVariable = useCallback((key: string) => {
    setVariables((prev) => {
      const updated = { ...prev };

      delete updated[key];

      return updated;
    });
  }, []);
  const handleUpdateVariableValue = useCallback(
    (key: string, newVal: string) => {
      setVariables((prev) => ({ ...prev, [key]: newVal }));
    },
    [],
  );
  const credentialKeyHints = useMemo(() => {
    const secretKeys = new Set<string>();
    const headerKeys = new Set<string>();
    const cookieNames = new Set<string>();

    for (const cred of applicationCredentials) {
      for (const s of cred.secrets) secretKeys.add(s.key);
      for (const h of cred.headers) headerKeys.add(h.key);
      for (const c of cred.cookies) cookieNames.add(c.name);
    }

    return {
      secretKeys: [...secretKeys],
      headerKeys: [...headerKeys],
      cookieNames: [...cookieNames],
    };
  }, [applicationCredentials]);
  const extensions = useMemo(
    () => [
      javascript(),
      getTerraEditorThemeByMode({ isDark }),
      createTerraAutocomplete(credentialKeyHints),
    ],
    [isDark, credentialKeyHints],
  );
  const variableEntries = Object.entries(variables);

  return (
    <VStack gap="2.5" alignItems="stretch">
      <HStack justify="space-between" alignItems="center">
        <Text fontSize="sm" fontWeight="medium">
          {isCreating ? 'Create New Script' : 'Edit Script'}
        </Text>
      </HStack>

      <Box>
        <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1">
          Name
        </Text>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., HMAC Request Signing"
          size="xs"
          autoFocus
        />
      </Box>

      <Box>
        <HStack gap="1.5" alignItems="center" mb="1">
          <SparklesIcon size={12} style={{ opacity: 0.6 }} />
          <Text fontSize="xs" fontWeight="medium" color="fg.muted">
            {script.trim() ? 'Edit with AI' : 'Generate with AI'}
          </Text>
        </HStack>
        <HStack gap="1.5">
          <Input
            size="xs"
            value={aiPrompt}
            onChange={(e) => {
              setAiPrompt(e.target.value);
              if (aiError) setAiError(null);
            }}
            placeholder={
              script.trim()
                ? 'e.g., "add a nonce header", "change signing to SHA-512"…'
                : 'Describe what the script should do…'
            }
            fontFamily="mono"
            fontSize="xs"
            flex="1"
            disabled={aiGenerating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleGenerate();
              }
            }}
          />
          <Tooltip
            title={
              script.trim()
                ? 'Modify existing script with AI'
                : 'Generate script with AI'
            }
          >
            <IconButton
              aria-label="Generate"
              size="xs"
              variant="ghost"
              onClick={handleGenerate}
              disabled={!aiPrompt.trim() || aiGenerating}
            >
              {aiGenerating ? (
                <Spinner size="sm" />
              ) : (
                <SparklesIcon size={14} />
              )}
            </IconButton>
          </Tooltip>
        </HStack>
        {aiError && (
          <Text fontSize="xs" color="fg.error" mt="1">
            {aiError}
          </Text>
        )}
      </Box>

      <Box>
        <HStack justify="space-between" mb="1">
          <Text fontSize="xs" fontWeight="medium" color="fg.muted">
            Script
          </Text>
          {script && !syntaxResult.valid && (
            <HStack gap="1" alignItems="center">
              <Alert01Icon size={12} color="var(--colors-fg-error)" />
              <Text fontSize="xs" color="fg.error">
                {syntaxResult.error}
              </Text>
            </HStack>
          )}
          {script && syntaxResult.valid && (
            <Text fontSize="xs" color="green.11">
              Syntax OK
            </Text>
          )}
        </HStack>

        <Box
          borderWidth="1px"
          borderColor={
            script && !syntaxResult.valid ? 'border.error' : 'border.default'
          }
          borderRadius="sm"
          overflow="hidden"
        >
          <CodeMirror
            value={script}
            onChange={setScript}
            extensions={extensions}
            placeholder="// Write your pre-request script here..."
            height="190px"
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              highlightActiveLine: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: false,
              indentOnInput: true,
            }}
          />
        </Box>
      </Box>

      <Box
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="sm"
        overflow="hidden"
      >
        <HStack
          px="2"
          py="1.5"
          gap="1.5"
          cursor="pointer"
          onClick={() => setShowVariables((prev) => !prev)}
          _hover={{ bg: 'bg.muted' }}
          justifyContent="space-between"
          bg="bg.subtle"
        >
          <HStack gap="2" alignItems="center">
            {showVariables ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
            <VariableIcon size={12} />
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              Variables
            </Text>
            {variableEntries.length > 0 && (
              <Text fontSize="xs" color="fg.subtle">
                ({variableEntries.length})
              </Text>
            )}
          </HStack>
        </HStack>

        {showVariables && (
          <Box px="2" py="2" maxH="160px" overflow="auto">
            <VStack gap="1.5" alignItems="flex-start" w="full">
              {variableEntries.map(([key, val]) => (
                <HStack key={key} gap="1.5" w="full" alignItems="center">
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    color="fg.muted"
                    minW="100px"
                    flexShrink={0}
                  >
                    {key}
                  </Text>
                  <Input
                    size="xs"
                    value={val}
                    onChange={(e) =>
                      handleUpdateVariableValue(key, e.target.value)
                    }
                    fontFamily="mono"
                    fontSize="xs"
                    flex="1"
                  />
                  <IconButton
                    aria-label={`Remove ${key}`}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleRemoveVariable(key)}
                  >
                    <Cancel01Icon size={14} />
                  </IconButton>
                </HStack>
              ))}
              <HStack gap="2" w="full" alignItems="center">
                <Input
                  size="xs"
                  value={newVarKey}
                  onChange={(e) => setNewVarKey(e.target.value)}
                  placeholder="key"
                  fontFamily="mono"
                  fontSize="xs"
                  minW="100px"
                  flexShrink={0}
                  w="100px"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddVariable();
                  }}
                />
                <Input
                  size="xs"
                  value={newVarValue}
                  onChange={(e) => setNewVarValue(e.target.value)}
                  placeholder="value"
                  fontFamily="mono"
                  fontSize="xs"
                  flex="1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddVariable();
                  }}
                />
                <Tooltip title="Add variable">
                  <IconButton
                    aria-label="Add variable"
                    size="xs"
                    variant="ghost"
                    onClick={handleAddVariable}
                    disabled={!newVarKey.trim()}
                  >
                    <Add01Icon size={14} />
                  </IconButton>
                </Tooltip>
              </HStack>
            </VStack>
          </Box>
        )}
      </Box>

      <CredentialSecretsSection
        credentials={credentialsWithData}
        showCredentials={showCredentials}
        onToggle={() => setShowCredentials((prev) => !prev)}
      />

      <HStack
        gap="2"
        justify="flex-end"
        pt="2.5"
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <Button type="button" variant="ghost" size="xs" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          size="xs"
          onClick={handleSave}
          disabled={!canSave}
        >
          {isCreating ? 'Create Script' : 'Save Changes'}
        </Button>
      </HStack>
    </VStack>
  );
}

function CredentialFieldRow({
  accessor,
  masked,
}: {
  accessor: string;
  masked?: boolean;
}) {
  return (
    <HStack gap="2" alignItems="center" pl="2">
      <Text fontSize="xs" fontFamily="mono" color="fg.muted" flex="1" truncate>
        {accessor}
      </Text>
      {masked && (
        <Text fontSize="xs" fontFamily="mono" color="fg.subtle">
          ••••••••
        </Text>
      )}
    </HStack>
  );
}

function CredentialSecretsSection({
  credentials,
  showCredentials,
  onToggle,
}: {
  credentials: ApplicationCredential[];
  showCredentials: boolean;
  onToggle: () => void;
}) {
  if (credentials.length === 0) return null;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      overflow="hidden"
    >
      <HStack
        px="3"
        py="2"
        gap="2"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'bg.muted' }}
        justifyContent="space-between"
        bg="bg.subtle"
      >
        <HStack gap="2" alignItems="center">
          {showCredentials ? (
            <ArrowDown01Icon size={12} />
          ) : (
            <ArrowRight01Icon size={12} />
          )}
          <Key01Icon size={12} />
          <Text fontSize="xs" fontWeight="medium" color="fg.muted">
            Credentials
          </Text>
          <Text fontSize="xs" color="fg.subtle">
            ({credentials.length})
          </Text>
        </HStack>
      </HStack>

      {showCredentials && (
        <Box px="3" py="3" maxH="250px" overflow="auto">
          <Text fontSize="xs" color="fg.subtle" mb="2">
            Read-only. The active credential from the replay toolbar is
            available via{' '}
            <Text as="span" fontFamily="mono">
              terra.credentials
            </Text>
            .
          </Text>
          <VStack gap="3" alignItems="stretch">
            {credentials.map((credential) => (
              <Box key={credential.id}>
                <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1">
                  {credential.username} ({credential.role})
                </Text>
                <VStack gap="0.5" alignItems="stretch">
                  <CredentialFieldRow accessor="terra.credentials.username" />
                  {credential.password && (
                    <CredentialFieldRow
                      accessor="terra.credentials.password"
                      masked
                    />
                  )}
                  <CredentialFieldRow accessor="terra.credentials.role" />
                  {credential.secrets.map((secret) => (
                    <CredentialFieldRow
                      key={`s-${secret.key}`}
                      accessor={`terra.credentials.secrets.get(${JSON.stringify(secret.key)})`}
                      masked
                    />
                  ))}
                  {credential.headers.map((header) => (
                    <CredentialFieldRow
                      key={`h-${header.key}`}
                      accessor={`terra.credentials.headers.get(${JSON.stringify(header.key)})`}
                      masked
                    />
                  ))}
                  {credential.cookies.map((cookie) => (
                    <CredentialFieldRow
                      key={`c-${cookie.name}`}
                      accessor={`terra.credentials.cookies.get(${JSON.stringify(cookie.name)})`}
                      masked
                    />
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
